/**
 * BondInfoSection - Coordinator for bond info grid, modal, and validation
 * Used in PostingDetailPage for managing individual power entries
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  getTransferPowers,
  createTransferPowers,
  updateTransferPowers,
  deleteTransferPowers,
} from '../../../../services/oathTransferBondService';
import { formatCurrency } from '../shared/formatUtils';
import { OathAlert, OathButton, OathSpinner } from '../../../shared/OathUI';
import PowersTable from '../shared/PowersTable';
import BondInfoModal from './BondInfoModal';

const BondInfoSection = ({ bondDetails, onRefresh }) => {
  const [powers, setPowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBondInfo, setSelectedBondInfo] = useState(null);
  const [totalPowerAmount, setTotalPowerAmount] = useState(0);

  const transactionId = bondDetails?.transferBondTransactionID;
  const agencyId = bondDetails?.executingAgentID;
  const canEdit = !bondDetails?.hasBeenPosted;

  // Fetch powers
  const fetchPowers = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    const response = await getTransferPowers(transactionId);
    if (response.success) {
      const data = response.data || [];
      setPowers(data);
      const total = data.reduce((sum, p) => sum + (parseFloat(p.bondAmount) || 0), 0);
      setTotalPowerAmount(total);
    }
    setLoading(false);
  }, [transactionId]);

  useEffect(() => {
    fetchPowers();
  }, [fetchPowers]);

  // Memoize already-used power IDs to prevent infinite re-fetch in BondInfoModal
  const alreadyUsedPowerIds = useMemo(
    () => powers.map((p) => p.powerId).filter(Boolean),
    [powers]
  );

  // Handle add — pre-populate defendant info from existing powers
  const handleAdd = () => {
    if (powers.length > 0) {
      const lastPower = powers[powers.length - 1];
      setSelectedBondInfo({
        caseNumber: lastPower.caseNumber || '',
        offense: lastPower.offense || '',
        offenseType: lastPower.offenseType || '',
        courtDate: lastPower.courtDate || '',
      });
    } else {
      setSelectedBondInfo(null);
    }
    setModalOpen(true);
  };

  // Handle edit
  const handleEdit = (power) => {
    setSelectedBondInfo(power);
    setModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (power) => {
    if (!window.confirm(`Delete power ${power.powerNumber || ''}? This cannot be undone.`)) return;

    const response = await deleteTransferPowers(transactionId, power.transferBondId || power.transferBondID);
    if (response.success) {
      toast.success('Transfer power deleted successfully.');
      fetchPowers();
      onRefresh?.();
    } else {
      toast.error(response.error || 'Failed to delete power.');
    }
  };

  // Handle save from modal
  const handleSave = async (bondData) => {
    if (bondData.transferBondId) {
      // Update existing
      const response = await updateTransferPowers(transactionId, bondData.transferBondId, bondData);
      if (response.success) {
        toast.success('Bond information updated successfully.');
        fetchPowers();
        onRefresh?.();
      } else {
        throw new Error(response.error);
      }
    } else {
      // Create new
      const response = await createTransferPowers(transactionId, bondData);
      if (response.success) {
        toast.success('Bond information saved successfully.');
        fetchPowers();
        onRefresh?.();
      } else {
        throw new Error(response.error);
      }
    }
  };

  const expectedAmount = bondDetails?.totalBondAmount || 0;
  const amountsMismatch = powers.length > 0 && Math.abs(totalPowerAmount - expectedAmount) > 0.01;

  return (
    <div className="space-y-4">
      {/* Header with Add button */}
      {canEdit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage individual power entries for this transfer bond.
          </p>
          <OathButton
            size="sm"
            icon={PlusIcon}
            onClick={handleAdd}
          >
            Add Power
          </OathButton>
        </div>
      )}

      {/* Amount validation */}
      {amountsMismatch && (
        <OathAlert variant="error">
          <p className="text-sm">
            Power amounts total <strong>{formatCurrency(totalPowerAmount)}</strong> but the bond amount is{' '}
            <strong>{formatCurrency(expectedAmount)}</strong>. These must match before submitting for verification.
          </p>
        </OathAlert>
      )}

      {/* Summary */}
      {powers.length > 0 && (
        <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
          <span>Powers: <strong className="text-gray-900 dark:text-white">{powers.length}</strong></span>
          <span>Total: <strong className="text-gray-900 dark:text-white">{formatCurrency(totalPowerAmount)}</strong></span>
          <span>Expected: <strong className="text-gray-900 dark:text-white">{formatCurrency(expectedAmount)}</strong></span>
        </div>
      )}

      {/* Powers table */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <OathSpinner size="md" />
        </div>
      ) : (
        <PowersTable
          powers={powers}
          showActions={canEdit}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emptyMessage="No powers assigned yet. Click 'Add Power' to get started."
        />
      )}

      {/* Bond Info Modal */}
      <BondInfoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        bondInfo={selectedBondInfo}
        agencyId={agencyId}
        transactionId={transactionId}
        alreadyUsedPowerIds={alreadyUsedPowerIds}
      />
    </div>
  );
};

export default BondInfoSection;
